import type { AppDispatch } from '../index';
import { TAG_DISPATCHERS } from './apiRegistry';

/**
 * Parses tag string into type and id.
 * Supports formats:
 *   "partner_campaigns"       → { type: "partner_campaigns", id: "LIST" }
 *   "partner_campaigns:LIST"  → { type: "partner_campaigns", id: "LIST" }
 *   "partner_campaigns:409"   → { type: "partner_campaigns", id: "409" }
 */
const parseTag = (tag: string): { type: string; id: string } => {
  const colonIdx = tag.indexOf(':');
  if (colonIdx === -1) {
    return { type: tag, id: 'LIST' };
  }
  return {
    type: tag.substring(0, colonIdx),
    id: tag.substring(colonIdx + 1),
  };
};

/**
 * Invalidates RTK Query cache for the given tags.
 * Accepts tags in "type" or "type:id" format.
 * Always also invalidates LIST to refresh list queries.
 */
export const invalidateTagsByName = (dispatch: AppDispatch, tags: string[]): void => {
  // Group tags by type, collecting all ids per type
  const tagsByType = new Map<string, Set<string>>();

  for (const tag of tags) {
    const { type, id } = parseTag(tag);
    if (!tagsByType.has(type)) {
      tagsByType.set(type, new Set());
    }
    tagsByType.get(type)!.add(id);
    // Always include LIST so list queries refresh too
    tagsByType.get(type)!.add('LIST');
  }

  // Dispatch invalidation per API
  for (const [type, ids] of tagsByType) {
    const entry = TAG_DISPATCHERS[type];
    if (entry) {
      const invalidationTags = Array.from(ids).map((id) => ({ type: entry.tagType, id }));
      dispatch(entry.api.util.invalidateTags(invalidationTags));
    }
  }
};
